/**
 * Risk Summary Section Component
 *
 * Displays an at-a-glance view of inventory items at risk of stocking out.
 * Features:
 * - Collapsible summary with severity breakdown (ordered by decreasing severity)
 * - Timeframe selector (1/3/6/12 months)
 * - Buffer months configuration
 * - Search filter for inventory/SKU
 * - List of at-risk items with RiskItemCard (sorted by severity)
 * - Expand/collapse and "View All" functionality
 * - Tooltips explaining severity levels
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { InventoryRiskItem, RiskSeverity, RiskTimeframe } from "../types";
import { SEVERITY_ORDER } from "models/InventoryModel";
import RiskItemCard from "./RiskItemCard";

interface RiskSummarySectionProps {
  riskItems: InventoryRiskItem[];
  isLoading: boolean;
  timeframeMonths: RiskTimeframe;
  onTimeframeChange: (value: RiskTimeframe) => void;
  bufferMonths: number;
  onBufferMonthsChange: (value: number) => void;
  onViewInTable: (groupingKey: string) => void;
  onLeadTimeChange: (inventoryId: string, newLeadTimeMonths: number) => void;
  ignoredKeys: Set<string>;
  ignoringKey: string | null;
  onToggleIgnore: (inventoryId: string, country: string) => void;
}


const SEVERITY_CHIP_CONFIG: Record<RiskSeverity, { bg: string; label: string; tooltip: string }> = {
  CRITICAL: {
    bg: "#c62828",
    label: "critical",
    tooltip: "Stock-out imminent (less than 1 month)"
  },
  "AT-RISK": {
    bg: "#e65100",
    label: "at-risk",
    tooltip: "Stock-out approaching (1-2 months)"
  },
  WATCH: {
    bg: "#f9a825",
    label: "watch",
    tooltip: "Stock-out possible (2-3 months)"
  },
  OK: {
    bg: "#2e7d32",
    label: "ok",
    tooltip: "No immediate concern (more than 3 months)"
  },
};

const DEFAULT_VISIBLE_COUNT = 5;
const DEFAULT_MIN_DEMAND_UNITS = 5;
// New key on purpose: the old "inventoryRiskMinDailyRate" values (units/day)
// are on a different scale than units per 120-day window.
const LOCAL_STORAGE_KEY = "inventoryRiskMinDemandUnits";

// Demand evidence for the dormancy filter: units sold on the days an item
// actually had stock. A rate filter on dailyRunRate hid every item already out
// of stock (its trailing rate is 0), so real sellers mid-stock-out vanished.
//
// The server caps this at 120 days but measures it over the ledger it was
// asked to build, which the page's date range bounds — about 93 days early in
// a month, 123 late. So the window is "the loaded history", not a guaranteed
// 120 days, and the chip label says so.
function demandUnits(item: InventoryRiskItem): number {
  // No in-stock day in that range means the item was never measured at all, so
  // dropping it would be a false negative rather than a judgement about demand.
  if (item.demandInStockDays === 0) return Infinity;

  // Deploy-gap fallback, for while the API doesn't send demandUnitsSold yet.
  // dailyRunRate is a trailing 7-day average, so it stands in for demand only
  // while an item is in stock — a stocked-out item reads exactly 0 and would be
  // hidden again, which is the blindness this filter exists to remove. Remove
  // this branch once the collector change is live.
  return item.demandUnitsSold ?? (item.currentInventory <= 0 ? Infinity : item.dailyRunRate * 120);
}

function getStoredMinDemandUnits(): number {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored !== null) {
      const value = parseFloat(stored);
      if (!isNaN(value) && value >= 0) return value;
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_MIN_DEMAND_UNITS;
}

function setStoredMinDemandUnits(value: number): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, String(value));
  } catch {
    // localStorage not available
  }
}

export default function RiskSummarySection({
  riskItems,
  isLoading,
  timeframeMonths,
  onTimeframeChange,
  bufferMonths,
  onBufferMonthsChange,
  onViewInTable,
  onLeadTimeChange,
  ignoredKeys,
  ignoringKey,
  onToggleIgnore,
}: RiskSummarySectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [bufferInput, setBufferInput] = useState(String(bufferMonths));
  const [isEditingBuffer, setIsEditingBuffer] = useState(false);
  const [timeframeInput, setTimeframeInput] = useState(String(timeframeMonths));
  const [isEditingTimeframe, setIsEditingTimeframe] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [minDemandUnits, setMinDemandUnits] = useState<number>(getStoredMinDemandUnits);
  const [minDemandUnitsInput, setMinDemandUnitsInput] = useState(String(getStoredMinDemandUnits()));
  const [isEditingMinRate, setIsEditingMinRate] = useState(false);

  // Filter items by demand evidence and search text
  const filteredRiskItems = useMemo(() => {
    let filtered = riskItems;

    // Apply demand-evidence filter (dormant SKUs out, temporarily-OOS sellers in)
    if (minDemandUnits > 0) {
      filtered = filtered.filter((item) => demandUnits(item) >= minDemandUnits);
    }

    // Apply search filter
    if (searchFilter.trim()) {
      const normalizedSearch = searchFilter.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const searchableText = `${item.inventory_id} ${item.inventory_name || ""}`.toLowerCase();
        const matchesName = searchableText.includes(normalizedSearch);
        const matchesSku = (item.skus || []).some(sku => sku.toLowerCase().includes(normalizedSearch));
        return matchesName || matchesSku;
      });
    }

    return filtered;
  }, [riskItems, searchFilter, minDemandUnits]);

  // Sort items: non-ignored first by severity, then ignored at the bottom
  const sortedRiskItems = useMemo(() => {
    return [...filteredRiskItems].sort((a, b) => {
      // Primary sort: ignored items go to the bottom
      const aIgnored = ignoredKeys.has(`${a.inventory_id}||${a.country}`);
      const bIgnored = ignoredKeys.has(`${b.inventory_id}||${b.country}`);
      if (aIgnored !== bIgnored) return aIgnored ? 1 : -1;
      // Secondary sort: severity (CRITICAL first)
      const severityDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      if (severityDiff !== 0) return severityDiff;
      // Tertiary sort: volume/projected sales (descending - highest first)
      return b.projectedSalesInPeriod - a.projectedSalesInPeriod;
    });
  }, [filteredRiskItems, ignoredKeys]);

  // Count by severity (using volume-filtered items for summary, not search-filtered)
  const volumeFilteredItems = useMemo(() => {
    if (minDemandUnits > 0) {
      return riskItems.filter((item) => demandUnits(item) >= minDemandUnits);
    }
    return riskItems;
  }, [riskItems, minDemandUnits]);

  const criticalCount = volumeFilteredItems.filter((item) => item.severity === "CRITICAL").length;
  const atRiskCount = volumeFilteredItems.filter((item) => item.severity === "AT-RISK").length;
  const watchCount = volumeFilteredItems.filter((item) => item.severity === "WATCH").length;
  const totalAtRisk = volumeFilteredItems.length;

  // Items to display
  const visibleItems = showAll ? sortedRiskItems : sortedRiskItems.slice(0, DEFAULT_VISIBLE_COUNT);
  const hasMore = sortedRiskItems.length > DEFAULT_VISIBLE_COUNT;

  const handleBufferBlur = () => {
    setIsEditingBuffer(false);
    const newValue = parseInt(bufferInput, 10);
    if (!isNaN(newValue) && newValue >= 0 && newValue !== bufferMonths) {
      onBufferMonthsChange(newValue);
    } else {
      setBufferInput(String(bufferMonths));
    }
  };

  const handleBufferKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBufferBlur();
    } else if (e.key === "Escape") {
      setBufferInput(String(bufferMonths));
      setIsEditingBuffer(false);
    }
  };

  const handleTimeframeBlur = () => {
    setIsEditingTimeframe(false);
    const newValue = parseFloat(timeframeInput);
    if (!isNaN(newValue) && newValue > 0 && newValue !== timeframeMonths) {
      onTimeframeChange(newValue);
    } else {
      setTimeframeInput(String(timeframeMonths));
    }
  };

  const handleTimeframeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTimeframeBlur();
    } else if (e.key === "Escape") {
      setTimeframeInput(String(timeframeMonths));
      setIsEditingTimeframe(false);
    }
  };

  const handleMinRateBlur = () => {
    setIsEditingMinRate(false);
    const newValue = parseFloat(minDemandUnitsInput);
    if (!isNaN(newValue) && newValue >= 0 && newValue !== minDemandUnits) {
      setMinDemandUnits(newValue);
      setStoredMinDemandUnits(newValue);
    } else {
      setMinDemandUnitsInput(String(minDemandUnits));
    }
  };

  const handleMinRateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleMinRateBlur();
    } else if (e.key === "Escape") {
      setMinDemandUnitsInput(String(minDemandUnits));
      setIsEditingMinRate(false);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Skeleton variant="rectangular" width={200} height={32} />
          <Skeleton variant="rectangular" width={120} height={32} />
          <Skeleton variant="rectangular" width={100} height={32} />
        </Box>
        <Skeleton variant="rectangular" height={100} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ mb: 3, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          backgroundColor: "#F8FAFC",
          borderBottom: isExpanded ? "1px solid #E2E8F0" : "none",
          flexWrap: "wrap",
        }}
      >
        {/* Title */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Inventory Risk Summary
        </Typography>

        {/* Timeframe selector */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Stock out within:
          </Typography>
          {isEditingTimeframe ? (
            <TextField
              size="small"
              type="number"
              value={timeframeInput}
              onChange={(e) => setTimeframeInput(e.target.value)}
              onBlur={handleTimeframeBlur}
              onKeyDown={handleTimeframeKeyDown}
              autoFocus
              inputProps={{ min: 1, step: 1, style: { padding: "4px 8px", width: 50 } }}
              sx={{ "& .MuiOutlinedInput-root": { height: 28 } }}
            />
          ) : (
            <Tooltip title="Click to edit stock-out timeframe (months)">
              <Chip
                label={`${timeframeMonths} ${timeframeMonths === 1 ? 'month' : 'months'}`}
                size="small"
                onClick={() => setIsEditingTimeframe(true)}
                sx={{ cursor: "pointer", height: 24 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Buffer months */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Buffer:
          </Typography>
          {isEditingBuffer ? (
            <TextField
              size="small"
              type="number"
              value={bufferInput}
              onChange={(e) => setBufferInput(e.target.value)}
              onBlur={handleBufferBlur}
              onKeyDown={handleBufferKeyDown}
              autoFocus
              inputProps={{ min: 0, style: { padding: "4px 8px", width: 50 } }}
              sx={{ "& .MuiOutlinedInput-root": { height: 28 } }}
            />
          ) : (
            <Tooltip title="Click to edit buffer (months before stock-out to place order)">
              <Chip
                label={`${bufferMonths} ${bufferMonths === 1 ? 'month' : 'months'}`}
                size="small"
                onClick={() => setIsEditingBuffer(true)}
                sx={{ cursor: "pointer", height: 24 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Minimum volume filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Min volume:
          </Typography>
          {isEditingMinRate ? (
            <TextField
              size="small"
              type="number"
              value={minDemandUnitsInput}
              onChange={(e) => setMinDemandUnitsInput(e.target.value)}
              onBlur={handleMinRateBlur}
              onKeyDown={handleMinRateKeyDown}
              autoFocus
              inputProps={{ min: 0, step: 1, style: { padding: "4px 8px", width: 50 } }}
              sx={{ "& .MuiOutlinedInput-root": { height: 28 } }}
            />
          ) : (
            <Tooltip title="Minimum units sold while in stock, over the sales history loaded for the current date range (about 3 months by default). Filters out dormant SKUs without hiding items that are temporarily out of stock.">
              <Chip
                label={`${minDemandUnits} units sold`}
                size="small"
                onClick={() => setIsEditingMinRate(true)}
                sx={{ cursor: "pointer", height: 24 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Severity summary chips - ordered by decreasing severity */}
        <Box sx={{ display: "flex", gap: 1, flex: 1, flexWrap: "wrap" }}>
          {criticalCount > 0 && (
            <Tooltip title={SEVERITY_CHIP_CONFIG.CRITICAL.tooltip}>
              <Chip
                label={`${criticalCount} ${SEVERITY_CHIP_CONFIG.CRITICAL.label}`}
                size="small"
                sx={{ backgroundColor: SEVERITY_CHIP_CONFIG.CRITICAL.bg, color: "#fff" }}
              />
            </Tooltip>
          )}
          {atRiskCount > 0 && (
            <Tooltip title={SEVERITY_CHIP_CONFIG["AT-RISK"].tooltip}>
              <Chip
                label={`${atRiskCount} ${SEVERITY_CHIP_CONFIG["AT-RISK"].label}`}
                size="small"
                sx={{ backgroundColor: SEVERITY_CHIP_CONFIG["AT-RISK"].bg, color: "#fff" }}
              />
            </Tooltip>
          )}
          {watchCount > 0 && (
            <Tooltip title={SEVERITY_CHIP_CONFIG.WATCH.tooltip}>
              <Chip
                label={`${watchCount} ${SEVERITY_CHIP_CONFIG.WATCH.label}`}
                size="small"
                sx={{ backgroundColor: SEVERITY_CHIP_CONFIG.WATCH.bg, color: "#fff" }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Expand/collapse toggle */}
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ ml: "auto" }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Collapsible content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {totalAtRisk === 0 ? (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", py: 2, opacity: 0.7 }}
            >
              No items at risk of stocking out within the selected timeframe.
            </Typography>
          ) : (
            <>
              {/* Search filter */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  label="Filter by Inventory or SKU"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Search by name, ID, or SKU..."
                  sx={{ width: 300 }}
                />
                {searchFilter && filteredRiskItems.length !== riskItems.length && (
                  <Typography variant="caption" sx={{ ml: 2, opacity: 0.7 }}>
                    Showing {filteredRiskItems.length} of {riskItems.length} items
                  </Typography>
                )}
              </Box>

              {/* Risk item cards */}
              {sortedRiskItems.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", py: 2, opacity: 0.7 }}
                >
                  No items match the search filter.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        lg: "1fr 1fr 1fr",
                      },
                      gap: 1.5,
                    }}
                  >
                    {visibleItems.map((item) => {
                      const itemKey = `${item.inventory_id}||${item.country}`;
                      return (
                        <RiskItemCard
                          key={item.groupingKey}
                          item={item}
                          isIgnored={ignoredKeys.has(itemKey)}
                          isIgnoreLoading={ignoringKey === itemKey}
                          onViewInTable={onViewInTable}
                          onLeadTimeChange={onLeadTimeChange}
                          onToggleIgnore={onToggleIgnore}
                        />
                      );
                    })}
                  </Box>

                  {/* View All button */}
                  {hasMore && (
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll
                          ? "Show Less"
                          : `View All ${sortedRiskItems.length} Items`}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
